/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

import * as React from 'react';

// constants
import { ROLES } from '@config/constants/roles';

import { TAcl } from '@global-types/app/app';
import { useWhoAmI } from 'api/get-whoami';

/* -------------------------------------------------------------------------- */
/*                                    Type                                    */
/* -------------------------------------------------------------------------- */
type RoleTypes = keyof typeof ROLES;

/* -------------------------------------------------------------------------- */
/*                                    Lib                                     */
/* -------------------------------------------------------------------------- */

type RoleObject = {
	role: RoleTypes;
	ACLs: string[];
	organization?: string;
};

export function findPriorityRole(
	roles: RoleObject[]
): RoleObject | undefined {
	const priorities = [
		ROLES.SUPERADMIN,
		ROLES.ADMIN,
		ROLES.STAFF,
		ROLES.ORGANIZATION_ADMIN,
		ROLES.ORGANIZATION_STAFF,
		ROLES.ORGANIZATION_MEMBER,
	];

	const subPriorities = new Set<string>([
		ROLES.BRAND_ADMIN,
		ROLES.BRAND_STAFF,
		ROLES.PRODUCER_ADMIN,
		ROLES.PRODUCER_STAFF,
	]);

	for (const priority of priorities) {
		const found = roles.find((r) => r.role === priority);
		if (found) {
			if (found.role === ROLES.ORGANIZATION_MEMBER) {
				const secondRole = roles.find((r) =>
					subPriorities.has(r.role)
				);
				return secondRole;
			} else {
				return found;
			}
		}
	}
	return undefined;
}

// Hook export checkAccess function & Role variable
export const useAuthorization = () => {
	const { data, isFetched } = useWhoAmI();

	const highestPriorityRole = React.useMemo(() => {
		if (!data?.roles || !Array.isArray(data.roles)) {
			return null;
		}
		return findPriorityRole(data.roles);
	}, [data]);

	const role: RoleTypes =
		highestPriorityRole?.role ?? ROLES.USER;

	const primaryRole = React.useMemo(() => {
		if (!data || !Array.isArray(data.roles)) {
			return null;
		}
		return data.roles.find((r) => r.role === role);
	}, [data, role]);

	const mergedAcls = React.useMemo(() => {
		const aclSet = new Set<string>();
		if (data?.roles && Array.isArray(data.roles)) {
			data.roles.forEach((item) => {
				if (Array.isArray(item.ACLs)) {
					item.ACLs.forEach((acl) => aclSet.add(acl));
				}
			});
		}
		return aclSet;
	}, [data]);

	const checkAccess = React.useCallback(
		({
			allowedRoles,
			allowedAcl,
		}: {
			allowedRoles: string[];
			allowedAcl?: string;
		}) => {
			if (!isFetched || !data) {
				return false;
			}
			if (allowedRoles.length > 0) {
				const allowedRolesSet = new Set(allowedRoles);
				if (allowedAcl) {
					return (
						allowedRolesSet.has(role) &&
						mergedAcls.has(allowedAcl)
					);
				}
				return allowedRolesSet.has(role);
			}
			return true;
		},
		[data, isFetched, mergedAcls, role]
	);

	const checkRole = React.useCallback(
		(allowedRoles: string[]) => {
			if (allowedRoles.length === 0) {
				return false;
			}
			const allowedRolesSet = new Set(allowedRoles);
			return allowedRolesSet.has(role);
		},
		[role]
	);

	const checkAcl = React.useCallback(
		(allowedAcl: string) => {
			return mergedAcls.has(allowedAcl);
		},
		[mergedAcls]
	);

	const checkAcls = React.useCallback(
		(allowedAcl: TAcl[]) => {
			if (
				mergedAcls.size === 0 ||
				allowedAcl.length === 0
			) {
				return false;
			}
			return allowedAcl.every((acl) =>
				mergedAcls.has(acl as string)
			);
		},
		[mergedAcls]
	);

	const hasAnyAcl = React.useCallback(
		(allowedAcl: TAcl[]) => {
			if (
				mergedAcls.size === 0 ||
				allowedAcl.length === 0
			) {
				return false;
			}

			// OR condition : au moins un ACL doit exister
			return allowedAcl.some((acl) =>
				mergedAcls.has(acl as string)
			);
		},
		[mergedAcls]
	);

	return {
		hasAnyAcl,
		checkAcl,
		checkAccess,
		checkRole,
		checkAcls,
		role,
		user: data,
		entity: primaryRole?.entity
			? primaryRole.entity
			: null,
	};
};

type AuthorizationProps = {
	forbiddenFallback?: React.ReactNode;
	children: React.ReactNode;
} & (
	| {
			allowedRoles: RoleTypes[];
			policyCheck?: never;
	  }
	| {
			allowedRoles?: never;
			policyCheck: boolean;
	  }
);

export const Authorization = ({
	policyCheck,
	allowedRoles,
	forbiddenFallback = null,
	children,
}: AuthorizationProps) => {
	const { checkAccess } = useAuthorization();

	let canAccess = false;

	if (allowedRoles) {
		canAccess = checkAccess({ allowedRoles });
	}

	if (typeof policyCheck !== 'undefined') {
		canAccess = policyCheck;
	}

	return <>{canAccess ? children : forbiddenFallback}</>;
};
